// Commitment visibility dropdown — options + Private auto-assign rule.
//
// Surfaces the three options the dropdown renders (Team-visible,
// Private, Employer-visible / disabled) and the small piece of
// behaviour shared between the create modal, the form page, and the
// detail surface: when a Commitment becomes Private, the assignee
// snaps to the current user; switching back to Team-visible just
// re-enables the assignee field without overwriting the value.

import type {
  CommitmentDetail,
  CommitmentFormVisibility,
} from "./types";


export interface CommitmentVisibilityOption {
  value: CommitmentFormVisibility | "employer";
  label: string;
  /** Disabled in the dropdown (employer-visible is not active yet);
   * the dropdown still shows it so users see what's coming. */
  disabled?: boolean;
  /** Hint surfaced next to the option label — drives the "Coming
   * soon" copy on the employer entry. */
  hint?: string;
}


export const COMMITMENT_VISIBILITY_OPTIONS:
  readonly CommitmentVisibilityOption[] = [
  { value: "team", label: "Team-visible" },
  { value: "private", label: "Private" },
  {
    value: "employer",
    label: "Employer-visible",
    disabled: true,
    hint: "Coming soon",
  },
];


/** Resolve the assignee fields after a visibility change.
 *
 * Private snaps assigned_to to the current user (and clears the
 * job-title / client-contact assignment branches because Private is
 * always assigned to a user). Team-visible leaves the existing
 * assignee untouched — the user picked them deliberately.
 *
 * Returns ``null`` when the visibility transition does not require
 * any assignee change so the caller can skip the extra ``onChange``.
 */
export function resolveAssigneeForVisibility(args: {
  nextVisibility: CommitmentFormVisibility;
  currentAssignedTo: string;
  currentUserId: string | null;
}): {
  assigned_to: string;
  assigned_job_title: string;
  assigned_client_contact_id: string;
} | null {
  if (args.nextVisibility !== "private") return null;
  if (!args.currentUserId) return null;
  if (args.currentAssignedTo === args.currentUserId) return null;
  return {
    assigned_to: args.currentUserId,
    assigned_job_title: "",
    assigned_client_contact_id: "",
  };
}


/** Reasons a Commitment cannot be flipped to Private on the detail
 * surface. Mirrors the backend ``_apply_visibility_rules`` gates so
 * the UI can disable the option pre-emptively; the BE remains the
 * source of truth and rejects forbidden flips. */
export type PrivateBlockReason =
  | "not_creator"
  | "playbook_source"
  | "inactive";


export const PRIVATE_BLOCK_COPY: Record<PrivateBlockReason, string> = {
  not_creator: "Only the creator can make this Commitment private.",
  playbook_source:
    "Playbook-generated Commitments must remain team-visible.",
  inactive: "Only active Commitments can be made private.",
};


/** Decide whether the current user may flip the given Commitment to
 * Private. Returns ``{ ok: true }`` when allowed; otherwise returns
 * the first matching block reason in the precedence order
 * creator → playbook → active.
 *
 * Already-private rows always return ``{ ok: true }`` — the dropdown
 * still needs to let the user flip back to Team-visible without
 * surfacing a misleading "can't make private" reason while sitting
 * on the Private value. */
export function canMakePrivate(args: {
  detail: Pick<
    CommitmentDetail,
    | "created_by_id"
    | "source"
    | "archived_at"
    | "status"
    | "completed_at"
    | "cancelled_at"
    | "visibility"
  >;
  currentUserId: string | null;
}): { ok: true } | { ok: false; reason: PrivateBlockReason } {
  const { detail } = args;
  if (detail.visibility === "private") return { ok: true };
  if (
    !args.currentUserId
    || !detail.created_by_id
    || detail.created_by_id !== args.currentUserId
  ) {
    return { ok: false, reason: "not_creator" };
  }
  if (detail.source === "playbook") {
    return { ok: false, reason: "playbook_source" };
  }
  if (
    detail.archived_at
    || detail.status === "done"
    || detail.status === "cancelled"
  ) {
    return { ok: false, reason: "inactive" };
  }
  return { ok: true };
}


/** True when flipping the given Commitment to Private will reassign
 * it away from someone other than the current user. The detail
 * surface uses this to gate a confirmation modal — the safety rule
 * "you cannot take someone else's work private" deserves an explicit
 * yes from the user before the PATCH fires. */
export function privateReassignmentNeedsConfirmation(args: {
  currentAssignedTo: string | null | undefined;
  currentUserId: string | null;
}): boolean {
  const target = args.currentAssignedTo ?? "";
  if (!target) return false;
  if (!args.currentUserId) return false;
  return target !== args.currentUserId;
}


/** Private Commitments never carry a reviewer — the BE enforces this
 * and returns "Private Commitments cannot have reviewers." when a
 * client tries to set one. The form layer mirrors the rule so the
 * picker is locked + reviewer state is wiped when Private is selected. */
export function shouldClearReviewerForVisibility(
  nextVisibility: CommitmentFormVisibility,
): boolean {
  return nextVisibility === "private";
}


/** Helper for the reviewer-vs-assignee invariant: Team-visible
 * Commitments cannot share assignee + reviewer. Used by the picker
 * filter and the auto-clear branch when an assignee change would
 * collide with the current reviewer. */
export function reviewerCollidesWithAssignee(args: {
  assignedTo: string | null | undefined;
  reviewerId: string | null | undefined;
}): boolean {
  const a = args.assignedTo ?? "";
  const r = args.reviewerId ?? "";
  if (!a || !r) return false;
  return a === r;
}
